<template>
  <div class="modern-filters-container">
    <!-- Compact Single Row Layout -->
    <div class="filters-compact-row">
      <!-- Search Filter Group -->
      <div class="filter-group search-group">
        <label class="filter-label">{{ $t('knowledge_base_search_label') }}</label>
        <div class="search-input-wrapper">
          <ds-input
            :model-value="searchQuery"
            @update:model-value="$emit('update:searchQuery', $event)"
            icon="search"
            :placeholder="placeholder"
            is-simplified
            class="compact-search-input"
          />
        </div>
      </div>

      <!-- Slot for additional filters -->
      <slot name="filters"></slot>

      <!-- Actions Group -->
      <div class="filter-actions" v-if="hasActiveFilters">
        <button
          @click="$emit('clear-all')"
          class="clear-filters-btn"
          :title="$t('knowledge_base_filters_clear_filters_button', { count: activeFiltersCount })"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Active Filters Section -->
    <div class="active-filters-section" v-if="hasActiveFilters">
      <div class="active-filters-label">{{ $t('challenges_active_filters') }}</div>
      <div class="active-filters-tags">
        <!-- Slot for custom filter tags -->
        <slot name="active-filters"></slot>

        <!-- Search Filter Tag -->
        <span
          v-if="searchQuery.trim()"
          class="active-filter-tag"
          @click="$emit('clear-search')"
        >
          {{ $t('knowledge_base_search_label') }}: "{{ searchQuery }}"
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import DsInput from '../Form/DsInput.vue'

  export default defineComponent({
    name: 'ModernFiltersBar',
    components: {
      DsInput
    },
    props: {
      searchQuery: {
        type: String,
        default: ''
      },
      placeholder: {
        type: String,
        default: 'Search...'
      },
      hasCustomFilters: {
        type: Boolean,
        default: false
      },
      customFiltersCount: {
        type: Number,
        default: 0
      }
    },
    emits: ['update:searchQuery', 'clear-search', 'clear-all'],
    computed: {
      hasActiveFilters(): boolean {
        return this.searchQuery.trim() !== '' || this.hasCustomFilters
      },
      activeFiltersCount(): number {
        let count = this.searchQuery.trim() !== '' ? 1 : 0
        return count + this.customFiltersCount
      }
    }
  })
</script>

<style lang="scss" scoped>
  /* Modern Filters Styling */
  .modern-filters-container {
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    padding: 0.75rem;
    margin: 1rem 0;
    border: 1px solid #e5e7eb;

    .filter-label {
      font-weight: 500;
      font-size: 0.75rem;
      color: #6b7280;
      white-space: nowrap;
      text-transform: uppercase;
      letter-spacing: 0.025em;
    }

    .filters-compact-row {
      display: flex;
      align-items: center;
      gap: 1rem;
      flex-wrap: wrap;

      .filter-group {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        min-width: 120px;

        &.search-group {
          flex: 1;
          min-width: 200px;

          .search-input-wrapper {
            flex: 1;
          }
        }

        .filter-label {
          font-weight: 500;
          font-size: 0.75rem;
          color: #6b7280;
          white-space: nowrap;
          text-transform: uppercase;
          letter-spacing: 0.025em;
        }
      }

      .filter-actions {
        margin-left: auto;
        display: flex;
        align-items: center;
        gap: 0.5rem;

        .clear-filters-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          background: #f3f4f6;
          color: #6b7280;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover {
            background: #e5e7eb;
            color: #374151;
            border-color: #d1d5db;
          }

          svg {
            width: 14px;
            height: 14px;
          }
        }
      }
    }

    .active-filters-section {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding-top: 0.75rem;
      margin-top: 0.75rem;
      border-top: 1px solid #f3f4f6;

      .active-filters-label {
        font-weight: 500;
        font-size: 0.75rem;
        color: #6b7280;
        white-space: nowrap;
        text-transform: uppercase;
        letter-spacing: 0.025em;
      }

      .active-filters-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        flex: 1;

        .active-filter-tag {
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          background: var(--primary-community, #3b82f6);
          color: white;
          border: 1px solid var(--primary-community, #3b82f6);
          border-radius: 16px;
          padding: 0.25rem 0.625rem;
          font-size: 0.75rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;

          &:hover {
            background: var(--primary-community-lighter, #dbeafe);
            border-color: var(--primary-community-lighter, #dbeafe);
            transform: translateY(-1px);
          }

          svg {
            width: 12px;
            height: 12px;
            opacity: 0.8;
          }

          &:hover svg {
            opacity: 1;
          }
        }
      }
    }
  }

  // Responsive design
  @media (max-width: 768px) {
    .modern-filters-container {
      padding: 0.5rem;

      .filters-compact-row {
        flex-direction: column;
        align-items: stretch;
        gap: 0.75rem;

        .filter-group {
          flex-direction: column;
          align-items: stretch;
          gap: 0.25rem;

          &.search-group {
            min-width: auto;
          }

          .filter-label {
            font-size: 0.7rem;
          }
        }

        .filter-actions {
          margin-left: 0;
          align-self: flex-end;
        }
      }
    }
  }
</style>
